import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type PublicTeamSummary } from '../lib/api';
import { Heading } from '../ui/heading';
import { Text } from '../ui/text';
import { SkeletonCards } from '../components/PageChrome';
import FollowButton from '../components/FollowButton';

interface PublicTeamsResponse {
  teams: PublicTeamSummary[];
}

export default function Teams() {
  const [teams, setTeams] = useState<PublicTeamsResponse['teams']>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.listTeamsPublic();
        if (!cancelled) setTeams(data.teams);
      } catch (err) {
        if (!cancelled) setError(String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Heading>Teams</Heading>
          <Text className="mt-2 max-w-2xl">
            Open a team page to see if they are live, ask to follow, and watch past games when replays are ready.
          </Text>
        </div>
      </header>

      {error ? (
        <Text className="text-red-400">Error: {error}</Text>
      ) : teams === undefined ? (
        <SkeletonCards count={4} />
      ) : teams.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-800 p-8 text-center">
          <Heading level={2} className="text-lg">
            No public teams yet
          </Heading>
          <Text className="mt-2">Teams will appear here once they are active.</Text>
        </div>
      ) : (
        <ul className="grid gap-4 md:grid-cols-2">
          {teams.map((t) => (
            <li
              key={t.teamId}
              className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 transition hover:border-zinc-700"
            >
              <Link to={`/teams/${t.teamId}`} className="block p-5">
                <div className="flex items-start gap-4">
                  <div
                    className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-emerald-400/10 text-sm font-semibold text-emerald-200"
                    style={{
                      backgroundColor: t.primaryColor ? `${t.primaryColor}22` : undefined,
                      color: t.secondaryColor ?? undefined,
                    }}
                  >
                    {t.logoUrl ? (
                      <img src={t.logoUrl} alt="" className="size-full object-cover" />
                    ) : (
                      t.name
                        .split(/\s+/)
                        .map((part) => part[0])
                        .join('')
                        .slice(0, 2)
                    )}
                  </div>
                  <div className="min-w-0">
                    <Heading level={3} className="text-lg">
                      {t.name}
                    </Heading>
                    <Text className="mt-1 text-sm">
                      {t.season ? t.season : 'Season not set'}
                    </Text>
                    <Text className="mt-3 font-mono text-xs text-zinc-500">
                      {t.teamId}
                    </Text>
                  </div>
                </div>
              </Link>
              <div className="border-t border-zinc-800 p-3">
                <FollowButton teamId={t.teamId} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
